import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { Grid, Typography, Divider, Box } from '@mui/material'
import PrintInput from 'components/generic/printable/components/PrintInput'
import PrintLocationInfo from 'components/generic/printable/components/PrintLocationInfo'
import PrintSummaryInfo from 'components/generic/printable/components/PrintSummaryInfo'
import PrintTicketsSummary from 'components/generic/printable/components/PrintTicketsSummary'
import PrintTicketsFreightSummary from 'components/generic/printable/components/PrintTicketsFreightSummary'
import PrintTicketsGrid from 'components/generic/printable/components/PrintTicketsGrid'
import PrintPaymentsTable from 'components/generic/printable/components/PrintPaymentsTable'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { setDecimalPrecision } from 'utils/helpers'
import { PRINT_DOCUMENT_TYPES, BILL_TYPES } from 'utils/constants'

const headers = ['Material Name', 'Gross', 'Tare', 'Deductions', 'Net', 'Price', 'Value']

const freightHeaders = ['Ticket #', 'Order #', 'Material', 'Net Weight', 'Freight Cost']

const PrintableBill = ({
  customerData,
  paymentsData,
  billData,
  billTaxRate,
  notes,
  summaryData,
  tickets,
  priceEntriesByPurchaseOrderId,
  priceSheet,
  defaultPriceSheet,
  pricePrepend,
}) => {
  const printMethods = useForm()
  const [customerAddress, setCustomerAddress] = useState({})
  const { showWeightDecimals } = useSelector(state => state.settings?.settings?.purchaseSettings?.inboundTickets || {})
  const billType = useMemo(() => billData?.type || BILL_TYPES.MATERIAL.id, [billData])
  const { settings } = useSelector(state => state.settings)
  const { enablePrintedDocCompliance } = useSelector(state => state.featureFlags?.variables || {})

  useEffect(() => {
    if (customerData) {
      const { locations } = customerData
      const payToLocation = locations.find(location => location.isDefaultPayToLocation) ?? locations[0]
      setCustomerAddress(payToLocation)
    }
  }, [customerData])

  useEffect(() => {
    printMethods.reset({
      completionDate: dateFormatter(billData?.completionDate || billData?.createdAt) || ' ',
      notes,
    })
  }, [customerData])

  const { defaultYardUm } = useSelector(state => state.settings)

  return (
    <>
      <Grid mb={2} container wrap="nowrap">
        <Grid item xs={billType === BILL_TYPES.MATERIAL.id ? 3 : 4}>
          <PrintLocationInfo title1="Paid to" location1={{ ...customerAddress, name: customerData?.displayName }} />
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={billType === BILL_TYPES.MATERIAL.id ? 5 : 4} pl={2}>
          <Typography variant="h5">Details</Typography>
          <Grid mt={1} container>
            <PrintInput
              name="completionDate"
              label="COMPLETION DATE"
              control={printMethods.control}
              xs={6}
              InputLabelProps={{ shrink: true }}
            />
            <PrintInput
              name="notes"
              label="NOTES"
              control={printMethods.control}
              xs={12}
              multiline
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs={4}>
          <PrintSummaryInfo
            summaryInfo={[
              {
                label: 'Total:',
                value: `${pricePrepend}${setDecimalPrecision(summaryData.totalValue, { decimalPrecisionOverride: 2 })}`,
              },
              {
                label: 'Paid:',
                value: `${pricePrepend}${setDecimalPrecision(summaryData.totalValue - summaryData.remainingBalance, {
                  decimalPrecisionOverride: 2,
                })}`,
              },
              {
                label: 'Balance:',
                value: `${pricePrepend}${setDecimalPrecision(summaryData.remainingBalance, { decimalPrecisionOverride: 2 })}`,
              },
              {
                label: 'Gross:',
                value: `${setDecimalPrecision(summaryData.totalGross, {
                  removeTrailingZeros: true,
                  decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
                })} ${defaultYardUm}`,
              },
              {
                label: 'Tare:',
                value: `${setDecimalPrecision(summaryData.totalTare, {
                  removeTrailingZeros: true,
                  decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
                })} ${defaultYardUm}`,
              },
              {
                label: 'Adjustments:',
                value: `${setDecimalPrecision(summaryData.totalAdjustments, {
                  removeTrailingZeros: true,
                  decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
                })} ${defaultYardUm}`,
              },
              {
                label: 'Net:',
                value: `${setDecimalPrecision(summaryData.totalNet, {
                  removeTrailingZeros: true,
                  decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
                })} ${defaultYardUm}`,
              },
            ]}
          />
        </Grid>
      </Grid>
      {(billData?.paymentIds || []).length > 0 ? (
        <Grid mb={2} wrap="nowrap">
          <PrintPaymentsTable
            title="Payments Made"
            paymentInfo={{
              Items: paymentsData?.map(payment => ({
                ...payment,
                paymentDate: payment.paymentDate?.split('T')?.[0],
                paymentType: payment.paymentMethod,
                amountPaid: payment.total,
              })),
            }}
            pricePrepend={pricePrepend}
          />
        </Grid>
      ) : null}
      {billType === BILL_TYPES.MATERIAL.id ? (
        <>
          <PrintTicketsSummary
            title="Summary"
            headers={headers}
            tickets={tickets || []}
            taxRate={billTaxRate}
            priceEntriesByPurchaseOrderId={priceEntriesByPurchaseOrderId}
            priceSheet={priceSheet}
            defaultPriceSheet={defaultPriceSheet}
            defaultPriceType="buyPrice"
            showDeductions
            showWeightDecimals={showWeightDecimals}
            pricePrepend={pricePrepend}
            appliedRoundingDelta={Number(billData?.appliedRoundingDelta || 0)}
          />
          <Box mt={5} borderBottom="1px">
            <Typography variant="body1">Accepted [ &nbsp;&nbsp;&nbsp;&nbsp; ]</Typography>
            <Divider sx={{ width: '100%' }} />
          </Box>
          <div id="disclaimer">
            <Box mt={2}>
              <Typography variant="body1">Disclaimer</Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '8px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {settings?.documentSettings?.general?.purchaseTicketDisclaimer || ''}
              </Typography>
            </Box>
          </div>
          <div className="pagebreak">
            <PrintTicketsGrid
              type={PRINT_DOCUMENT_TYPES.BILL}
              title={`Tickets (${billData?.numberOfTickets || 0})`}
              headers={headers}
              tickets={tickets || []}
              priceEntriesByPurchaseOrderId={priceEntriesByPurchaseOrderId}
              priceSheet={priceSheet}
              defaultPriceSheet={defaultPriceSheet}
              defaultPriceType="buyPrice"
              showDeductions
              showWeightDecimals={showWeightDecimals}
              showMaterialDeductionNotes
              isPayment
              pricePrepend={pricePrepend}
              showTruckTotal
            />
            <div id="disclaimer">
              <Box mt={2}>
                <Typography variant="body1">Disclaimer</Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '8px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {settings?.documentSettings?.general?.purchaseTicketDisclaimer || ''}
                </Typography>
              </Box>
            </div>
            {enablePrintedDocCompliance && (
              <div id="weighmaster-certificate">
                <Box mt={2}>
                  <Typography variant="body1">WEIGHMASTER CERTIFICATE</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '8px',
                    }}
                  >
                    {settings?.documentSettings?.general?.weighmasterCertificate || ''}
                  </Typography>
                </Box>
                <Box mt={2} sx={{ borderBottom: '1px dotted lightgray' }} />
              </div>
            )}
          </div>
        </>
      ) : (
        <PrintTicketsFreightSummary
          title="Summary"
          headers={freightHeaders}
          tickets={tickets || []}
          taxRate={billTaxRate}
          showDeductions
          showWeightDecimals={showWeightDecimals}
          pricePrepend={pricePrepend}
        />
      )}
    </>
  )
}

PrintableBill.propTypes = {
  customerData: PropTypes.object.isRequired,
  paymentsData: PropTypes.array,
  billData: PropTypes.object.isRequired,
  billTaxRate: PropTypes.number,
  notes: PropTypes.string,
  summaryData: PropTypes.object.isRequired,
  tickets: PropTypes.array.isRequired,
  priceEntriesByPurchaseOrderId: PropTypes.object,
  priceSheet: PropTypes.object,
  defaultPriceSheet: PropTypes.object.isRequired,
  pricePrepend: PropTypes.string,
}

PrintableBill.defaultProps = {
  paymentsData: [],
  billTaxRate: 0,
  priceSheet: null,
  priceEntriesByPurchaseOrderId: null,
  pricePrepend: '$',
  notes: '',
}

export default PrintableBill
